import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Download, ChevronUp, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '../../components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { formatDateString } from '../../lib/utils';

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  birthdate: string;
  sex: string;
  age_group_id: string | null;
  age_group?: {
    id: string;
    group_name: string;
    sex: string;
  };
}

type SortField = 'playerName' | 'birthdate' | 'sex' | 'ageGroup';
type SortDirection = 'asc' | 'desc';

export default function MismatchedAgeGroupsReport() {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('playerName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  async function fetchMismatchedPlayers() {
    setLoading(true);
    setError('');
    setReportGenerated(true);
    setPlayers([]);

    try {
      // Query 1: Players with assigned age groups, filtered in JS for sex / birthdate mismatches
      const { data: assignedData, error: assignedError } = await supabase
        .from('players')
        .select(`
          id,
          first_name,
          last_name,
          birthdate,
          sex,
          age_group_id,
          age_group:age_groups(id, group_name, sex, birth_date_min, birth_date_max)
        `)
        .not('age_group_id', 'is', null);

      if (assignedError) {
        throw assignedError;
      }

      const mismatchedData = (assignedData || []).filter(player => {
        const ageGroup = Array.isArray(player.age_group) ? player.age_group[0] : player.age_group;
        if (!ageGroup) return true;
        return (
          player.sex !== ageGroup.sex ||
          new Date(player.birthdate) < new Date(ageGroup.birth_date_min) ||
          new Date(player.birthdate) > new Date(ageGroup.birth_date_max)
        );
      }).map(player => ({
        ...player,
        age_group: Array.isArray(player.age_group) ? player.age_group[0] : player.age_group,
      }));

      // Query 2: Players with null age_group_id
      const { data: nullAgeGroupData, error: nullError } = await supabase
        .from('players')
        .select(`
          id,
          first_name,
          last_name,
          birthdate,
          sex,
          age_group_id
        `)
        .is('age_group_id', null);

      if (nullError) {
        throw nullError;
      }

      // Combine results
      const combinedData = [
        ...mismatchedData,
        ...(nullAgeGroupData || []).map(player => ({
          ...player,
          age_group: null
        }))
      ];

      setPlayers(combinedData);
    } catch (err: any) {
      console.error('Error fetching mismatched players:', err);
      setError(err.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedPlayers = () => {
    return [...players].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'playerName':
          const aName = `${a.last_name}, ${a.first_name}`;
          const bName = `${b.last_name}, ${b.first_name}`;
          comparison = aName.localeCompare(bName);
          break;
        case 'birthdate':
          comparison = new Date(a.birthdate).getTime() - new Date(b.birthdate).getTime();
          break;
        case 'sex':
          comparison = a.sex.localeCompare(b.sex);
          break;
        case 'ageGroup':
          const aGroup = a.age_group ? `${a.age_group.sex === 'male' ? 'Boys' : 'Girls'} ${a.age_group.group_name}` : 'Not Assigned';
          const bGroup = b.age_group ? `${b.age_group.sex === 'male' ? 'Boys' : 'Girls'} ${b.age_group.group_name}` : 'Not Assigned';
          comparison = aGroup.localeCompare(bGroup);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const exportCSV = () => {
    if (players.length === 0) return;
    
    const headers = ['Player Name', 'Birthdate', 'Sex', 'Age Group'];
    const sortedData = getSortedPlayers();

    const csvContent = [
      headers.join(','),
      ...sortedData.map(player => [
        `"${player.first_name} ${player.last_name}"`,
        `"${formatDateString(player.birthdate, 'en-US', 'short')}"`,
        `"${player.sex}"`,
        `"${player.age_group ? `${player.age_group.sex === 'male' ? 'Boys' : 'Girls'} ${player.age_group.group_name}` : 'Not Assigned'}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `mismatched_age_groups_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="ml-1 h-4 w-4 text-gray-400" />;
    return sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Players with Mismatched Age Groups</CardTitle>
        <CardDescription>Players whose age group is null or does not match their birthdate or gender.</CardDescription>
        <div className="flex justify-end space-x-2">
          <Button onClick={fetchMismatchedPlayers} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
          {reportGenerated && players.length > 0 && (
            <Button variant="outline" onClick={exportCSV} disabled={players.length === 0}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading && <p>Loading...</p>}
        {error && <p className="text-red-500">Error: {error}</p>}
        {reportGenerated && !loading && players.length === 0 && (
          <p>No players found with mismatched age groups.</p>
        )}
        {reportGenerated && !loading && players.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer" onClick={() => handleSort('playerName')}>
                  Player Name <SortIcon field="playerName" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('birthdate')}>
                  Birthdate <SortIcon field="birthdate" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('sex')}>
                  Gender <SortIcon field="sex" />
                </TableHead>
                <TableHead className="cursor-pointer" onClick={() => handleSort('ageGroup')}>
                  Age Group <SortIcon field="ageGroup" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getSortedPlayers().map((player) => (
                <TableRow key={player.id}>
                  <TableCell>
                    <Link to={`/player/${player.id}/edit`} className="text-blue-600 hover:underline">
                      {player.first_name} {player.last_name}
                    </Link>
                  </TableCell>
                  <TableCell>{formatDateString(player.birthdate, 'en-US', 'short')}</TableCell>
                  <TableCell>{player.sex}</TableCell>
                  <TableCell>
                    {player.age_group ? `${player.age_group.sex === 'male' ? 'Boys' : 'Girls'} ${player.age_group.group_name}` : 'Not Assigned'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}